import React from "react";
import { Notifier } from "@airbrake/browser";

/**
 * React error boundary that reports uncaught render errors to Airbrake.
 *
 * WHY IT TAKES CONFIG AS A PROP
 * -----------------------------
 * This previously built its own config from `airbrakeConfig()` (marked deprecated), which reads
 * `NEXT_PUBLIC_AIRBRAKE_*` at BUILD time. The image build does not set those, so `projectId` came
 * out as `Number(undefined)` → `NaN` and `projectKey` as `undefined`. `new Notifier()` throws on a
 * missing projectId/projectKey, so constructing it unconditionally at the app root would have taken
 * down every page. Config now comes from `useRuntimeConfig()` — served by `/api/runtime-config`
 * from the container environment — and the notifier is only built when that config is usable.
 *
 * Until this was mounted, Airbrake project 540556 (`pa-www-next`) had never received a single
 * notice: the component existed and was referenced in docs, but nothing ever rendered it.
 */

export interface AirbrakeConfig {
  projectId: number;
  projectKey: string;
}

interface Props {
  children: React.ReactNode;
  /** From `useRuntimeConfig().config.airbrake`. Reporting is skipped when absent or unpopulated. */
  airbrake?: AirbrakeConfig;
  /** Airbrake `environment` label, e.g. the deployed host. */
  environment?: string;
}

interface State {
  hasError: boolean;
}

/** The runtime-config default is `{ projectId: 0, projectKey: "" }`, which must not build a Notifier. */
function isUsable(config?: AirbrakeConfig): config is AirbrakeConfig {
  return (
    !!config &&
    Number.isFinite(config.projectId) &&
    config.projectId > 0 &&
    !!config.projectKey
  );
}

class ErrorBoundary extends React.Component<Props, State> {
  private readonly airbrake: Notifier | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };

    if (isUsable(props.airbrake)) {
      try {
        this.airbrake = new Notifier({
          projectId: props.airbrake.projectId,
          projectKey: props.airbrake.projectKey,
          environment: props.environment,
        });
      } catch (err) {
        // Never let error *reporting* break the page it exists to report on.
        // eslint-disable-next-line no-console
        console.error("Airbrake notifier could not be initialised", err);
      }
    }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log regardless, so errors stay visible when Airbrake is unconfigured (local, previews).
    // eslint-disable-next-line no-console
    console.error("Uncaught render error", error, info);
    this.airbrake?.notify({ error, params: { info } });
  }

  render() {
    // Render a fallback rather than the children that just threw. The previous version returned
    // `this.props.children` unconditionally, so a persistently failing subtree would throw again on
    // every re-render: the boundary caught the error but changed nothing.
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center"
        >
          <h2 className="text-xl font-semibold">Something went wrong</h2>
          <p className="text-sm text-gray-600">
            This page hit an unexpected error. Reloading usually fixes it.
          </p>
          <button
            type="button"
            className="rounded bg-black px-4 py-2 text-white"
            onClick={() => this.setState({ hasError: false })}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
